import React, { createContext, useContext, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { UseMutateAsyncFunction, useMutation } from 'react-query';

import { Config } from '@nw/shared/config';

import {
  RefreshAuthTokensRequestType,
  RefreshAuthTokensResponseType
} from '../services/IbUserAccountsServices/APIService/types';
import {
  clearAccessToken,
  clearAuthDomainTokens,
  clearRefreshToken,
  getAccessToken,
  getRefreshToken,
  setAccessTokenToDomain,
  setRefreshTokenToDomain
} from '../utils/AccountsStorageUtils';
import { clearUserSessionAndReload } from '../utils/UserSessionUtils';

import { useIbUserAccountsRefreshTokenAPIService } from './IbUserAccounts/useIbUserAccountsRefreshTokenAPIService';

interface AccountsRefreshAuthTokensProviderProps {
  children: any;
}

interface AccountsRefreshAuthTokensContextTypes {
  refreshTokenApi: UseMutateAsyncFunction<
    RefreshAuthTokensResponseType,
    Error,
    RefreshAuthTokensRequestType
  >;
  getAccessToken: () => string;
  getRefreshToken: () => string;
}

export const AccountsRefreshAuthTokensContext =
  createContext<AccountsRefreshAuthTokensContextTypes | null>(null);

export const AccountsRefreshAuthTokenProvider = (
  props: AccountsRefreshAuthTokensProviderProps
) => {
  const refreshTokenPromise =
    useRef<Promise<RefreshAuthTokensResponseType> | null>(null);

  const { ibUserAccountsRefreshTokenAPIService } =
    useIbUserAccountsRefreshTokenAPIService();

  const { mutateAsync: refreshAuthTokensApi } = useMutation(
    async (requestObject: RefreshAuthTokensRequestType) => {
      if (refreshTokenPromise.current) {
        return refreshTokenPromise.current;
      }

      if (requestObject.refresh_token) {
        try {
          refreshTokenPromise.current =
            ibUserAccountsRefreshTokenAPIService.getRefreshToken(requestObject);

          const data = await refreshTokenPromise.current;

          Sentry.setContext('RefreshTokenHook', {
            authDomain: Config['AUTH_COOKIE_DOMAIN'],
            tokens: JSON.stringify(data),
            accessToken: data.access_token,
            refreshToken: data.refresh_token
          });

          clearAccessToken();
          clearRefreshToken();
          clearAuthDomainTokens();

          setAccessTokenToDomain(data.access_token);
          setRefreshTokenToDomain(data.refresh_token);

          Sentry.setContext('Refreshtoken set cookie', {
            authDomain: Config['AUTH_COOKIE_DOMAIN'],
            tokens: JSON.stringify(data),
            accessToken: data.access_token,
            refreshToken: data.refresh_token
          });
          Sentry.addBreadcrumb({
            type: 'info',
            category: 'use.refreshToken',
            message: 'Refreshtoken set cookie',
            data: {
              authDomain: Config['AUTH_COOKIE_DOMAIN'],
              tokens: JSON.stringify(data),
              accessToken: data.access_token,
              refreshToken: data.refresh_token
            }
          });
          Sentry.addBreadcrumb({
            type: 'info',
            category: 'use.refreshToken',
            message: 'In refresh token success',
            data: {
              accessToken: getAccessToken(),
              refreshToken: getRefreshToken()
            }
          });

          return data;
        } catch (e) {
          Sentry.setContext('RefreshTokenError', { message: e.message });
          Sentry.addBreadcrumb({
            type: 'info',
            category: 'use.refreshToken',
            message: 'RefreshTokenError',
            data: { message: e.message }
          });
          clearUserSessionAndReload();
          throw new Error(
            JSON.stringify({
              response: 'Failed to refresh authentication token',
              http_status_code: 400,
              res_status: 'REFRESH_TOKEN_ERROR'
            })
          );
        } finally {
          refreshTokenPromise.current = null;
        }
      } else {
        clearUserSessionAndReload();
        throw new Error(
          JSON.stringify({
            response: 'Invalid refresh token',
            http_status_code: 400,
            res_status: 'REFRESH_TOKEN_EXPIRED'
          })
        );
      }
    }
  );

  return (
    <AccountsRefreshAuthTokensContext.Provider
      value={{
        refreshTokenApi: refreshAuthTokensApi,
        getAccessToken,
        getRefreshToken
      }}
    >
      {props.children}
    </AccountsRefreshAuthTokensContext.Provider>
  );
};

const useAccountsRefreshAuthTokens =
  (): AccountsRefreshAuthTokensContextTypes => {
    const accountsRefreshAuthTokensContext = useContext(
      AccountsRefreshAuthTokensContext
    );

    if (!accountsRefreshAuthTokensContext)
      throw new Error(
        'Expected to be wrapped in an AccountsRefreshAuthTokensProvider'
      );

    return accountsRefreshAuthTokensContext;
  };

export { useAccountsRefreshAuthTokens };
